import {
    styled,
    Typography,
    TypographyProps,
    useTheme,
    SxProps,
    Theme,
    Box,
} from '@mui/material';
import useWindowWidth from 'components/hooks/useWindowWidth';
import Symbol from 'components/Symbol';
import { useMemo } from 'react';

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== '$disabled' &&
        prop !== '$noWrap' &&
        prop !== '$standalone' &&
        prop !== '$fullWidth' &&
        prop !== 'sx',
})<{
    $disabled?: boolean;
    $noWrap?: boolean;
    $standalone: boolean;
    $fullWidth?: boolean;
}>(({ theme, $disabled, $noWrap, $standalone, $fullWidth }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2),
    cursor: $disabled ? 'not-allowed' : 'pointer',
    userSelect: 'none',
    flexWrap: $noWrap ? 'nowrap' : 'wrap',
    ...($standalone && {
        width: 'max-content',
    }),
    ...($fullWidth && {
        width: '100%',
    }),
}));

const SwitchContainer = styled('div', {
    shouldForwardProp: prop => prop !== '$disableNull' && prop !== '$fullWidth',
})<{
    $disableNull: boolean;
    $fullWidth?: boolean;
}>(({ theme, $disableNull, $fullWidth }) => ({
    position: 'relative',
    display: 'grid',
    borderRadius: '8px',
    overflow: 'hidden',
    border: 'none',
    background: theme.palette.grey[200],
    gridTemplateColumns: `repeat(${$disableNull ? 2 : 3}, minmax(0, 1fr))`,
    zIndex: 0,
    ...($fullWidth && {
        flex: 1,
        width: '100%',
        minWidth: 0,
    }),
}));

const SlidingBackground = styled('div', {
    shouldForwardProp: prop =>
        !['$position', '$stateValue', '$disableNull', '$disabled'].includes(
            prop as string
        ),
})<{
    $position: 'left' | 'middle' | 'right' | 'none';
    $stateValue: boolean | null;
    $disableNull: boolean;
    $disabled: boolean;
}>(({ theme, $position, $stateValue, $disableNull, $disabled }) => {
    // Position calculations
    const width = $disableNull ? '50%' : 'calc(100% / 3)';
    const leftPosition = $disableNull
        ? $position === 'left'
            ? '0%'
            : $position === 'right'
              ? '50%'
              : '-100%'
        : $position === 'left'
          ? '0%'
          : $position === 'middle'
            ? 'calc(100% / 3)'
            : $position === 'right'
              ? 'calc(200% / 3)'
              : '-100%';

    // Background color logic
    let background = 'transparent'; // Default/hidden background
    if ($stateValue === false)
        background = theme.palette.error[700]; // Red for No
    else if ($stateValue === true)
        background = theme.palette.primary[500]; // Primary for Yes
    else if ($stateValue === null) background = theme.palette.grey[400]; // Grey for Null

    return {
        position: 'absolute',
        top: 0,
        left: leftPosition,
        width: width,
        height: '100%',
        background: background,
        borderRadius: '8px',
        transition: 'all 150ms ease-in-out',
        zIndex: 1,
        opacity: $disabled ? 0.65 : 1,
    };
});

const SwitchButton = styled('button', {
    shouldForwardProp: prop =>
        !['$height', '$padding', '$width'].includes(prop as string),
})<{
    $height: number;
    $padding: string;
    $width?: string;
}>(({ $height, $padding, $width }) => ({
    'position': 'relative',
    'height': `${$height}px`,
    'padding': $padding,
    'width': $width,
    'border': 'none',
    'background': 'transparent',
    'borderRadius': '8px',
    'cursor': 'pointer',
    'zIndex': 2,
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'center',
    'flexShrink': 0,
    '&:disabled': {
        cursor: 'not-allowed',
    },
}));

const Text = styled(Typography, {
    shouldForwardProp: prop => prop !== '$height' && prop !== '$color',
})<{
    $height: number;
    $color: string;
}>(({ $height, $color }) => ({
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    color: $color,
    userSelect: 'none',
    lineHeight: $height + 'px',
    height: $height,
    textAlign: 'center',
    transition: 'color 150ms ease-in-out',
}));

interface Config {
    height: number;
    padding: string;
    width?: string;
    activeColor: string;
    inactiveColor: string;
    textVariant: TypographyProps['variant'];
    textFontWeight: number;
}

export interface TristateSwitchProps {
    value?: boolean | null;
    label?: string;
    labelVariant?: TypographyProps['variant'];
    description?: string; // description as subtitle
    info?: React.ReactNode; // info as tooltip
    size?: 'small' | 'medium' | 'large'; // default to 'small', or 'medium' if asText is true
    onChange: (value: boolean | null) => void;
    asText?: boolean;
    disableNull?: boolean; // Use the BinarySwitch instead of setting this prop.
    disabled?: boolean;
    noWrap?: boolean;
    fullWidth?: boolean;
    sx?: SxProps<Theme>;
}

export const TristateSwitch = ({
    value: _value,
    label,
    labelVariant,
    description,
    info,
    size,
    onChange,
    asText = false,
    disableNull = false,
    disabled,
    noWrap,
    fullWidth,
    sx,
}: TristateSwitchProps) => {
    const theme = useTheme();
    const { isMobile } = useWindowWidth();
    if (!size) {
        size = isMobile || asText ? 'medium' : 'small';
    }

    // Binary state forced when using text
    if (asText) {
        disableNull = true;
    }
    if (_value === undefined) {
        _value = null;
    }

    // If null is disabled but value is null, convert to false
    const value = disableNull && _value === null ? false : _value;

    const config = useMemo((): Config => {
        const baseConfig = {
            activeColor: '#ffffff',
            inactiveColor: theme.palette.grey[400],
        };

        switch (size) {
            case 'small':
                return {
                    ...baseConfig,
                    height: 22,
                    padding: asText ? '0 12px' : '0',
                    width: asText ? undefined : '22px',
                    textVariant: 'caption',
                    textFontWeight: 700,
                };
            case 'medium':
                return {
                    ...baseConfig,
                    height: 28,
                    padding: '0 12px',
                    textVariant: 'caption',
                    textFontWeight: 700,
                };
            case 'large':
                return {
                    ...baseConfig,
                    height: 32,
                    padding: '0 16px',
                    textVariant: 'body2',
                    textFontWeight: 700,
                };
        }
    }, [size, asText, theme]);

    const symbolSize = size === 'small' ? 14 : 18;
    const buttonWidth = fullWidth ? undefined : config.width;

    const handleNoClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            onChange(false);
        }
    };

    const handleYesClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            onChange(true);
        }
    };

    const handleNullClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!disabled) {
            onChange(null);
        }
    };

    const handleBaseClick = () => {
        if (disabled) return;

        // Determine next value in cycle
        if (disableNull) {
            // Binary cycle: false → true → false
            onChange(!value);
        } else {
            // Tristate cycle: false → null → true → false
            if (value === false) onChange(null);
            else if (value === null) onChange(true);
            else if (value === true) onChange(false);
        }
    };

    return (
        <Base
            $disabled={disabled}
            $noWrap={noWrap}
            $standalone={!label && !description}
            $fullWidth={fullWidth}
            onClick={handleBaseClick}
            sx={sx}>
            {(label || description) && (
                <div>
                    {label && (
                        <Typography
                            variant={labelVariant}
                            color={disabled ? 'textSecondary' : 'textPrimary'}>
                            {label}
                        </Typography>
                    )}
                    {description && (
                        <Typography
                            variant='caption'
                            color={disabled ? 'textSecondary' : 'textPrimary'}>
                            {description}
                        </Typography>
                    )}
                </div>
            )}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                    width: fullWidth ? '100%' : undefined,
                }}>
                <SwitchContainer
                    $disableNull={disableNull}
                    $fullWidth={fullWidth}>
                    <SlidingBackground
                        $position={
                            value === false
                                ? 'left'
                                : value === null
                                  ? 'middle'
                                  : value === true
                                    ? 'right'
                                    : 'none'
                        }
                        $stateValue={value}
                        $disableNull={disableNull}
                        $disabled={disabled || false}
                    />
                    <SwitchButton
                        $height={config.height}
                        $padding={config.padding}
                        $width={buttonWidth}
                        onClick={handleNoClick}
                        disabled={disabled}>
                        {asText ? (
                            <Text
                                $height={config.height}
                                $color={
                                    value === false
                                        ? config.activeColor
                                        : config.inactiveColor
                                }
                                variant={config.textVariant}
                                fontWeight={config.textFontWeight}>
                                Nej
                            </Text>
                        ) : (
                            <Symbol
                                symbol='close'
                                color={
                                    value === false
                                        ? config.activeColor
                                        : config.inactiveColor
                                }
                                size='sm'
                                sx={{
                                    transition: 'color 150ms ease-in-out',
                                    fontSize: `${symbolSize}px !important`,
                                }}
                                bold={symbolSize === 14}
                                tooltip='Nej'
                            />
                        )}
                    </SwitchButton>
                    {!disableNull && !asText && (
                        <SwitchButton
                            $height={config.height}
                            $padding={config.padding}
                            $width={buttonWidth}
                            onClick={handleNullClick}
                            disabled={disabled}>
                            <Symbol
                                symbol='check_indeterminate_small'
                                color={
                                    value === null
                                        ? config.activeColor
                                        : config.inactiveColor
                                }
                                size='sm'
                                sx={{
                                    transition: 'color 150ms ease-in-out',
                                    fontSize: `${symbolSize}px !important`,
                                }}
                                bold={symbolSize === 14}
                                tooltip='Ignorera'
                            />
                        </SwitchButton>
                    )}
                    <SwitchButton
                        $height={config.height}
                        $padding={config.padding}
                        $width={buttonWidth}
                        onClick={handleYesClick}
                        disabled={disabled}>
                        {asText ? (
                            <Text
                                $height={config.height}
                                $color={
                                    value === true
                                        ? config.activeColor
                                        : config.inactiveColor
                                }
                                variant={config.textVariant}
                                fontWeight={config.textFontWeight}>
                                Ja
                            </Text>
                        ) : (
                            <Symbol
                                symbol='check'
                                color={
                                    value === true
                                        ? config.activeColor
                                        : config.inactiveColor
                                }
                                size='sm'
                                sx={{
                                    transition: 'color 150ms ease-in-out',
                                    fontSize: `${symbolSize}px !important`,
                                }}
                                bold={symbolSize === 14}
                                tooltip='Ja'
                            />
                        )}
                    </SwitchButton>
                </SwitchContainer>
                {info ? (
                    <Symbol
                        symbol='info'
                        color='textSecondary'
                        size='sm'
                        tooltip={info}
                    />
                ) : null}
            </Box>
        </Base>
    );
};
